import React, { useState, useEffect, useCallback, useMemo } from "react";
import { db } from "../../../db/firebase";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  deleteDoc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import EventModal from "./EventModal";
import ConfirmDeleteEvent from "../../../components/ConfirmationDeleteEvent";
import { useTranslation } from "react-i18next";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineArchiveBox,
  HiOutlineCalendarDays,
  HiOutlineChevronUpDown,
  HiOutlineMagnifyingGlass,
  HiOutlineClock,
  HiOutlineArrowPath,
  HiOutlineMusicalNote,
  HiOutlineUsers,
  HiOutlineArrowUturnLeft,
  HiOutlineArrowRight,
} from "react-icons/hi2";
import { useAuth } from "../../../context/AuthContext";
import {
  parseLocalDate,
  parseLocalDateTime,
  startOfLocalDay,
} from "../../../utils/parseLocalDate";

const EVENT_TYPES = ["Service", "Worship", "Planning", "Global", "Other"] as const;

type EventType = (typeof EVENT_TYPES)[number];

function parseEventDate(dateStr: string, timeStr?: string) {
  const base = parseLocalDateTime(dateStr, timeStr);
  if (Number.isNaN(base.getTime())) return null;
  return base;
}

function relativeLabel(
  dateStr: string,
  t: (key: string, opts?: Record<string, unknown>) => string
) {
  const eventDay = startOfLocalDay(parseLocalDate(dateStr));
  if (Number.isNaN(eventDay.getTime())) return "";
  const today = startOfLocalDay(new Date());
  const diffDays = Math.round(
    (eventDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays === 0) return t("today");
  if (diffDays === 1) return t("tomorrow");
  if (diffDays === -1) return t("yesterday");
  if (diffDays > 1 && diffDays <= 14) return t("inDays", { count: diffDays });
  if (diffDays < -1 && diffDays >= -14) return t("daysAgo", { count: Math.abs(diffDays) });
  return "";
}

const typeBadgeClass = (type: string) => {
  switch (type) {
    case "Service":
      return "bg-blue-500/10 text-blue-700 dark:text-blue-300";
    case "Worship":
      return "bg-[#5932EA]/10 text-[#5932EA]";
    case "Planning":
      return "bg-amber-500/10 text-amber-700 dark:text-amber-300";
    case "Global":
      return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
    default:
      return "bg-gray-500/10 text-gray-600 dark:text-gray-300";
  }
};

const Events: React.FC = () => {
  const { t } = useTranslation();
  const { organizationId } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<any | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<EventType | "all">("all");
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 2800);
    return () => window.clearTimeout(id);
  }, [toast]);

  const fetchEvents = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const eventsSnapshot = await getDocs(
        query(
          collection(db, "events"),
          where("organizationId", "==", organizationId),
          where("status", "==", showArchived ? "Archived" : "Active")
        )
      );
      const eventsData = eventsSnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      const sorted = [...eventsData].sort((a, b) => {
        const dateA = parseEventDate(a.date, a.time)?.getTime() ?? 0;
        const dateB = parseEventDate(b.date, b.time)?.getTime() ?? 0;
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      });
      setEvents(sorted);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  }, [organizationId, showArchived, sortOrder]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const filteredEvents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return events.filter((event) => {
      if (typeFilter !== "all" && event.type !== typeFilter) return false;
      if (!q) return true;
      return (
        event.title?.toLowerCase().includes(q) ||
        event.description?.toLowerCase().includes(q)
      );
    });
  }, [events, searchQuery, typeFilter]);

  const { upcoming, past } = useMemo(() => {
    if (showArchived) {
      return { upcoming: filteredEvents, past: [] as any[] };
    }
    const today = startOfLocalDay(new Date());
    const up: any[] = [];
    const pa: any[] = [];
    filteredEvents.forEach((event) => {
      const day = startOfLocalDay(parseLocalDate(event.date));
      if (!Number.isNaN(day.getTime()) && day.getTime() < today.getTime()) {
        pa.push(event);
      } else {
        up.push(event);
      }
    });
    return { upcoming: up, past: pa };
  }, [filteredEvents, showArchived]);

  const toggleEventStatus = async (event: any) => {
    try {
      const newStatus = event.status === "Archived" ? "Active" : "Archived";
      await updateDoc(doc(db, "events", event.id), { status: newStatus });
      setEvents((prev) => prev.filter((e) => e.id !== event.id));
      setToast(newStatus === "Active" ? t("eventActivated") : t("eventArchived"));
    } catch (error) {
      console.error("Error updating event status:", error);
      setToast(t("statusChangeFailed"));
    }
  };

  const handleCreate = () => {
    setCurrentEvent(null);
    setShowModal(true);
  };

  const handleEdit = (event: any) => {
    setCurrentEvent(event);
    setShowModal(true);
  };

  const confirmDeleteEvent = (event: any) => {
    setEventToDelete(event);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!eventToDelete) return;
    try {
      await deleteDoc(doc(db, "events", eventToDelete.id));
      setEvents((prev) => prev.filter((e) => e.id !== eventToDelete.id));
      setIsDeleteModalOpen(false);
      setEventToDelete(null);
      setToast(t("eventDeleted") || "Event deleted");
    } catch (error) {
      console.error("Error deleting event:", error);
      setToast(t("deleteFailed") || "Could not delete event");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setCurrentEvent(null);
  };

  const handleSave = async (event: any) => {
    if (currentEvent) {
      await updateDoc(doc(db, "events", currentEvent.id), event);
      setEvents((prev) =>
        prev.map((e) => (e.id === currentEvent.id ? { ...e, ...event } : e))
      );
      handleModalClose();
      setToast(t("eventUpdated"));
      return;
    }

    const newRef = await addDoc(collection(db, "events"), {
      ...event,
      organizationId,
      status: "Active",
    });
    handleModalClose();
    navigate(`/events/${newRef.id}`);
  };

  const handleSort = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const typeLabel = (type: string) => {
    const key = type.toLowerCase();
    const translated = t(key);
    return translated === key ? type : translated;
  };

  const renderEventCard = (event: any) => {
    const dateObj = parseLocalDate(event.date);
    const validDate = !Number.isNaN(dateObj.getTime());
    const month = validDate
      ? dateObj.toLocaleDateString(undefined, { month: "short" })
      : "—";
    const day = validDate ? dateObj.getDate() : "—";
    const weekday = validDate
      ? dateObj.toLocaleDateString(undefined, { weekday: "short" })
      : "";
    const relative = validDate ? relativeLabel(event.date, t) : "";
    const songCount = Array.isArray(event.songs) ? event.songs.length : 0;
    const peopleCount =
      (Array.isArray(event.leaders) ? event.leaders.length : 0) +
      (Array.isArray(event.members) ? event.members.length : 0);

    return (
      <li
        key={event.id}
        onClick={() => navigate(`/events/${event.id}`)}
        className="group cursor-pointer"
      >
        <div className="flex items-center gap-3 px-3.5 sm:px-4 py-3.5 hover:bg-[#5932EA]/5 dark:hover:bg-[#5932EA]/10 active:bg-[#5932EA]/8 transition-colors">
          <div className="shrink-0 w-12 h-12 sm:w-14 sm:h-[3.75rem] rounded-xl bg-[#5932EA]/10 text-[#5932EA] flex flex-col items-center justify-center leading-none">
            <span className="text-[10px] font-semibold uppercase tracking-wide opacity-80">
              {month}
            </span>
            <span className="text-base sm:text-xl font-bold tabular-nums mt-0.5">
              {day}
            </span>
            <span className="hidden sm:block text-[10px] opacity-70 mt-0.5">
              {weekday}
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-gray-100 group-hover:text-[#5932EA] transition-colors truncate">
                {event.title}
              </h2>
              {event.type && (
                <span
                  className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide ${typeBadgeClass(
                    event.type
                  )}`}
                >
                  {typeLabel(event.type)}
                </span>
              )}
            </div>

            <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 min-w-0">
              {event.time && (
                <span className="inline-flex items-center gap-1 shrink-0">
                  <HiOutlineClock className="w-3.5 h-3.5 text-[#5932EA]/70" />
                  {event.time}
                </span>
              )}
              {relative && (
                <>
                  {event.time && (
                    <span className="text-gray-300 dark:text-gray-600" aria-hidden>
                      ·
                    </span>
                  )}
                  <span className="font-medium text-[#5932EA] truncate">
                    {relative}
                  </span>
                </>
              )}
              {songCount > 0 && (
                <>
                  <span className="text-gray-300 dark:text-gray-600" aria-hidden>
                    ·
                  </span>
                  <span className="inline-flex items-center gap-0.5 shrink-0">
                    <HiOutlineMusicalNote className="w-3.5 h-3.5" />
                    {songCount}
                  </span>
                </>
              )}
              {peopleCount > 0 && (
                <>
                  <span className="hidden sm:inline text-gray-300 dark:text-gray-600" aria-hidden>
                    ·
                  </span>
                  <span className="hidden sm:inline-flex items-center gap-0.5 shrink-0">
                    <HiOutlineUsers className="w-3.5 h-3.5" />
                    {peopleCount}
                  </span>
                </>
              )}
              {event.isRecurring && (
                <span className="hidden sm:inline-flex items-center gap-0.5 shrink-0 text-gray-400">
                  <HiOutlineArrowPath className="w-3.5 h-3.5" />
                </span>
              )}
            </div>

            {event.description && (
              <p className="hidden sm:block text-sm text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-1">
                {event.description}
              </p>
            )}
          </div>

          <div
            className="flex items-center gap-0.5 shrink-0"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(event);
              }}
              className="p-2 rounded-xl text-gray-400 hover:text-[#5932EA] hover:bg-[#5932EA]/10 transition-colors touch-manipulation"
              title={t("edit")}
              aria-label={t("edit")}
            >
              <HiOutlinePencil className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleEventStatus(event);
              }}
              className="p-2 rounded-xl text-gray-400 hover:text-amber-600 hover:bg-amber-500/10 transition-colors touch-manipulation"
              title={showArchived ? t("restoreEvent") : t("archive")}
              aria-label={showArchived ? t("restoreEvent") : t("archive")}
            >
              {showArchived ? (
                <HiOutlineArrowUturnLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <HiOutlineArchiveBox className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                confirmDeleteEvent(event);
              }}
              className="p-2 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-500/10 transition-colors touch-manipulation"
              title={t("delete")}
              aria-label={t("delete")}
            >
              <HiOutlineTrash className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <HiOutlineArrowRight className="hidden sm:block w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-[#5932EA] transition-colors ml-0.5" />
          </div>
        </div>
      </li>
    );
  };

  const renderSection = (title: string, items: any[]) => {
    if (items.length === 0) return null;
    return (
      <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100/80 dark:border-gray-700/80 flex items-center gap-2">
          <div className="w-1 h-4 rounded-full bg-[#5932EA]" />
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            {title}
          </h3>
          <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
            {items.length}
          </span>
        </div>
        <ul className="divide-y divide-gray-100/90 dark:divide-gray-700/70">
          {items.map(renderEventCard)}
        </ul>
      </section>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4 py-2">
        <div className="h-10 w-48 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
        <div className="h-12 w-full bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-28 rounded-2xl bg-gray-200 dark:bg-gray-700 animate-pulse"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-sm font-medium shadow-lg max-w-[calc(100%-2rem)] text-center">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#5932EA]/10 text-[#5932EA] shrink-0">
            <HiOutlineCalendarDays className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 truncate">
              {t("events")}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
              {filteredEvents.length}{" "}
              {showArchived ? t("archivedEvents") : t("activeEvents")}
              {!showArchived && upcoming.length > 0
                ? ` · ${upcoming.length} ${t("upcoming")}`
                : ""}
            </p>
          </div>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center justify-center gap-1.5 sm:gap-2 px-3.5 sm:px-5 py-2.5 rounded-xl font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] transition-colors shadow-sm shrink-0 touch-manipulation"
        >
          <HiOutlinePlus className="w-5 h-5" />
          <span className="hidden sm:inline">{t("addEvent")}</span>
        </button>
      </div>

      <div className="space-y-3">
        <div className="relative">
          <HiOutlineMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("searchEvents")}
            className="w-full pl-11 pr-4 py-3 sm:py-2.5 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/80 dark:bg-gray-800/60 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-colors text-base sm:text-sm"
          />
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex flex-1 sm:flex-none p-1 rounded-xl bg-gray-100/80 dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setShowArchived(false)}
              className={`flex-1 sm:flex-none px-3.5 py-2 sm:py-1.5 rounded-lg text-sm font-medium transition-colors touch-manipulation ${
                !showArchived
                  ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {t("active")}
            </button>
            <button
              type="button"
              onClick={() => setShowArchived(true)}
              className={`flex-1 sm:flex-none px-3.5 py-2 sm:py-1.5 rounded-lg text-sm font-medium transition-colors touch-manipulation ${
                showArchived
                  ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {t("archived")}
            </button>
          </div>

          <button
            type="button"
            onClick={handleSort}
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 sm:py-2 rounded-xl font-medium border border-gray-200/80 dark:border-gray-600/80 text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-800/60 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm shrink-0 touch-manipulation"
          >
            <HiOutlineChevronUpDown className="w-5 h-5" />
            <span className="hidden sm:inline">
              {sortOrder === "asc" ? t("soonestFirst") : t("latestFirst")}
            </span>
          </button>
        </div>
      </div>

      <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto scrollbar-none">
        <div className="flex gap-2 min-w-max sm:min-w-0 sm:flex-wrap pb-0.5">
          <button
            type="button"
            onClick={() => setTypeFilter("all")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors touch-manipulation ${
              typeFilter === "all"
                ? "bg-[#5932EA] text-white"
                : "bg-white/80 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-600/80 text-gray-600 dark:text-gray-300"
            }`}
          >
            {t("allTypes")}
          </button>
          {EVENT_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setTypeFilter(type)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors touch-manipulation ${
                typeFilter === type
                  ? "bg-[#5932EA] text-white"
                  : "bg-white/80 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-600/80 text-gray-600 dark:text-gray-300"
              }`}
            >
              {typeLabel(type)}
            </button>
          ))}
        </div>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 sm:py-16 rounded-2xl border border-dashed border-gray-200 dark:border-gray-600 bg-white/60 dark:bg-gray-800/40">
          <HiOutlineCalendarDays className="w-14 h-14 sm:w-16 sm:h-16 text-gray-400 dark:text-gray-500 mb-3" />
          <p className="text-gray-600 dark:text-gray-400 font-medium text-center px-4 text-sm sm:text-base">
            {searchQuery || typeFilter !== "all"
              ? t("noEventsMatchFilters")
              : showArchived
                ? t("noArchivedEvents")
                : t("noEventsAvailable")}
          </p>
          {!showArchived && !searchQuery && typeFilter === "all" && (
            <button
              onClick={handleCreate}
              className="mt-4 flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] transition-colors touch-manipulation"
            >
              <HiOutlinePlus className="w-5 h-5" />
              {t("addEvent")}
            </button>
          )}
          {(searchQuery || typeFilter !== "all") && (
            <button
              onClick={() => {
                setSearchQuery("");
                setTypeFilter("all");
              }}
              className="mt-4 px-4 py-2 rounded-xl text-sm font-medium text-[#5932EA] hover:bg-[#5932EA]/10 transition-colors"
            >
              {t("clearFilters")}
            </button>
          )}
        </div>
      ) : showArchived ? (
        <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm overflow-hidden">
          <ul className="divide-y divide-gray-100/90 dark:divide-gray-700/70">
            {filteredEvents.map(renderEventCard)}
          </ul>
        </section>
      ) : (
        <div className="space-y-4 sm:space-y-5">
          {renderSection(t("upcoming"), upcoming)}
          {renderSection(t("pastEvents"), past)}
        </div>
      )}

      {showModal && (
        <EventModal
          event={currentEvent}
          onSave={handleSave}
          onClose={handleModalClose}
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmDeleteEvent
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDelete}
          itemName={eventToDelete ? eventToDelete.title : ""}
        />
      )}
    </div>
  );
};

export default Events;
